import { useQueryClient } from '@tanstack/react-query';
import { styled } from '@linaria/react';
import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router';
import {
  automationWorkflowRunsKey,
  useAutomationWorkflows,
  useDeleteAutomationWorkflow,
  useRunAutomationWorkflow,
} from '../../../../api/automations-workflows-client/automations-workflows-client.hooks';
import type { AutomationWorkflow } from '../../../../api/automations-workflows-client/automations-workflows-client.type';
import { toast } from '../../../../containers/notifications';
import { Button } from '../../../../ui-library/button/button';
import { Icon } from '../../../../ui-library/icon/icon';
import { Loader } from '../../../../ui-library/loader/loader';
import { Text } from '../../../../ui-library/typography/typography';
import { AgentViewHeader } from '../agent-view-header';
import { AgentAutomationCard } from './agent-automation-card';
import { useAutomationCounts, useAutomationGroups, useAutomationMutations } from './agent-automations.hooks';
import { AutomationFilter } from './agent-automations.type';

export function AgentAutomationsView() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { workflows, isLoading, error } = useAutomationWorkflows();
  const [filter, setFilter] = useState<AutomationFilter>(AutomationFilter.ALL);
  const counts = useAutomationCounts(workflows);
  const groups = useAutomationGroups(workflows, filter);
  const { busyWorkflowId, toggleAutomationActive } = useAutomationMutations();
  const deleteMutation = useDeleteAutomationWorkflow();
  const runMutation = useRunAutomationWorkflow();

  const handleNew = useCallback((): void => {
    navigate('/application/flora-agent/automations/new');
  }, [navigate]);

  const handleOpen = useCallback(
    (workflow: AutomationWorkflow): void => {
      navigate(`/application/flora-agent/automations/${workflow.id}`);
    },
    [navigate],
  );

  const handleEdit = useCallback(
    (workflow: AutomationWorkflow): void => {
      navigate(`/application/flora-agent/automations/${workflow.id}/edit`);
    },
    [navigate],
  );

  const handleDelete = useCallback(
    (workflow: AutomationWorkflow): void => {
      const confirmed = window.confirm(`Delete automation "${workflow.name}"? This cannot be undone.`);
      if (!confirmed) return;
      deleteMutation.mutate(workflow.id, {
        onSuccess: () => toast.success('Automation deleted'),
        onError: () => toast.error('Failed to delete automation'),
      });
    },
    [deleteMutation],
  );

  const handleRunNow = useCallback(
    (workflow: AutomationWorkflow): void => {
      runMutation.mutate(workflow.id, {
        onSuccess: () => {
          toast.success(`Queued a run for "${workflow.name}"`);
          // The runs view isn't mounted from here, so the hook's invalidate
          // can't refetch an inactive query — and the global refetchOnMount:
          // false would then serve the stale cache (missing this run) on the
          // next visit. Drop the cached runs so the runs page loads fresh.
          queryClient.removeQueries({ queryKey: automationWorkflowRunsKey(workflow.id) });
        },
        onError: (mutationError) => toast.error(mutationError.message || 'Failed to queue run'),
      });
    },
    [runMutation, queryClient],
  );

  if (isLoading) {
    return (
      <Container>
        <MainColumn>
          <AgentViewHeader title="Automations" />
          <LoadingWrapper>
            <Loader />
          </LoadingWrapper>
        </MainColumn>
      </Container>
    );
  }

  if (error) {
    return (
      <Container>
        <MainColumn>
          <AgentViewHeader title="Automations" />
          <EmptyState>
            <Icon name="error" color="var(--ink-lighter)" size={48} />
            <Text size="regular" color="var(--ink-lighter)" style={{ marginTop: 12 }}>
              Failed to load automations
            </Text>
          </EmptyState>
        </MainColumn>
      </Container>
    );
  }

  return (
    <Container>
      <MainColumn>
        <HeaderRow>
          <AgentViewHeader
            title="Automations"
            subtitle={`${counts.all} ${counts.all === 1 ? 'automation' : 'automations'}`}
          />
          <Button variant="secondary" size="sm" onClick={handleNew}>
            <Icon name="add" color="#ffffff" size={16} style={{ marginRight: 4, cursor: 'pointer' }} />
            New Automation
          </Button>
        </HeaderRow>

        <FilterRow>
          <FilterChip
            $active={filter === AutomationFilter.ALL}
            aria-pressed={filter === AutomationFilter.ALL}
            onClick={() => setFilter(AutomationFilter.ALL)}
          >
            All <Count>{counts.all}</Count>
          </FilterChip>
          <FilterChip
            $active={filter === AutomationFilter.ACTIVE}
            aria-pressed={filter === AutomationFilter.ACTIVE}
            onClick={() => setFilter(AutomationFilter.ACTIVE)}
          >
            Active <Count>{counts.active}</Count>
          </FilterChip>
          <FilterChip
            $active={filter === AutomationFilter.PAUSED}
            aria-pressed={filter === AutomationFilter.PAUSED}
            onClick={() => setFilter(AutomationFilter.PAUSED)}
          >
            Paused <Count>{counts.paused}</Count>
          </FilterChip>
        </FilterRow>

        {workflows.length === 0 ? (
          <EmptyState>
            <Icon name="bolt" color="var(--ink-lighter)" size={48} />
            <Text size="regular" color="var(--ink-lighter)" style={{ marginTop: 12 }}>
              No automations yet
            </Text>
            <Text size="small" color="var(--ink-lighter)">
              Create an automation to react to Bloomfilter events.
            </Text>
            <Button variant="secondary" size="sm" onClick={handleNew} style={{ marginTop: 16 }}>
              <Icon name="add" color="#ffffff" size={16} style={{ marginRight: 4, cursor: 'pointer' }} />
              New Automation
            </Button>
          </EmptyState>
        ) : groups.length === 0 ? (
          <EmptyState>
            <Text size="regular" color="var(--ink-lighter)">
              No automations match this filter
            </Text>
          </EmptyState>
        ) : (
          <ScrollArea>
            {groups.map((group) => (
              <GroupSection key={group.key}>
                <GroupLabel>
                  <Text size="tiny" weight="bold" color="var(--ink-lighter)">
                    {group.label}
                  </Text>
                </GroupLabel>
                <AutomationList>
                  {group.workflows.map((workflow) => (
                    <AgentAutomationCard
                      key={workflow.id}
                      workflow={workflow}
                      isBusy={busyWorkflowId === workflow.id}
                      onOpen={handleOpen}
                      onEdit={handleEdit}
                      onToggleActive={toggleAutomationActive}
                      onRunNow={handleRunNow}
                      onDelete={handleDelete}
                    />
                  ))}
                </AutomationList>
              </GroupSection>
            ))}
          </ScrollArea>
        )}
      </MainColumn>
    </Container>
  );
}

const Container = styled.div`
  display: flex;
  flex-direction: row;
  height: 100%;
  width: 100%;
`;

const MainColumn = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
  padding: 40px 0;
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding-right: 80px;
`;

const FilterRow = styled.div`
  display: flex;
  gap: 8px;
  padding: 0 80px;
  margin-bottom: 20px;
`;

const FilterChip = styled.button<{ $active: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 14px;
  border-radius: 999px;
  border: 1px solid ${({ $active }) => ($active ? 'var(--secondary-base)' : 'var(--sky-light)')};
  background: ${({ $active }) => ($active ? 'rgba(87, 92, 229, 0.08)' : 'var(--base-white)')};
  color: ${({ $active }) => ($active ? 'var(--secondary-base)' : 'var(--ink-dark)')};
  font-size: 13px;
  cursor: pointer;
  transition: background 0.12s, border-color 0.12s;

  &:hover {
    border-color: var(--secondary-base);
  }
`;

const Count = styled.span`
  font-size: 11px;
  color: var(--ink-lighter);
`;

const ScrollArea = styled.div`
  overflow-y: auto;
  flex: 1;
  padding: 0 80px;
`;

const GroupSection = styled.div`
  margin-bottom: 24px;
`;

const GroupLabel = styled.div`
  margin-bottom: 12px;
  padding-left: 4px;
`;

const AutomationList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const LoadingWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 200px;
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  flex: 1;
  text-align: center;
  gap: 4px;
  padding: 0 80px;
`;
